<template>
  <div v-show="loadingConsent">
    <div class="progress-bar progress-bar-striped active" role="progressbar"
    aria-valuenow="100" aria-valuemin="0" aria-valuemax="100" style="width:100%">
      Loading&hellip;
    </div>
  </div>

  <div v-show="!loadingConsent">
    <h3><a href="/consent-forms">Consents</a>: {{ consent.name }}</h3>
    <div class="version-addendum">
      <span class="version">Latest Version: {{ consent.latestVersion.version }}</span>
        <a class="btn btn-default btn-xs add-version" @click.stop="addVersion" title="Add Version">
          <span class="glyphicon glyphicon-plus"></span>
        </a>
              
        <span class="addendum">
          Addendum: {{ consent.latestVersion.addendum || '--' }}
        </span>
        <a class="btn btn-default btn-xs add-addendum" @click.stop="addAddendum" title="Add Addendum">
          <span class="glyphicon glyphicon-plus"></span>
        </a>
    </div>

    <hr />

    <h3 class="participants">Participants</h3>

    <div class="alert alert-info" v-show="loadingParticipants">Loading&hellip;</div>

    <div class="form-horizontal">
      <button class="btn btn-default pull-right" @click="assignConsentTask" :disabled="checkedParticipants.length < 1">Assign Consent Task</button>
      <div class="form-group">
        <label for="filter" class="control-label col-sm-1"><strong>Filter:</strong></label>
        <div class="col-sm-3">
          <input type="text" id="filter" class="form-control" v-model="filter" placeholder="Type to Search"/>
        </div>
      </div>
    </div>

    <table class="table table-striped" id="consent-detail-table">
      <thead>
        <tr>
          <th @click="setSortBy('ccs_id')">
            CCS ID
          </th>
          <th @click="setSortBy('full_name')">
            Name
          </th>
          <th @click="setSortBy('age')">
            Age
          </th>
          <th @click="setSortBy('gender')">
            Gender
          </th>
          <th @click="setSortBy('language')">
            Language
          </th>
          <th @click="setSortBy('sab')">
            SAB
          </th>
          <th @click="setSortBy('serostatus')">
            Serostatus
          </th>
          <th @click="setSortBy('latest_signed')">
            Latest Version Signed
          </th>
          <th @click="setSortBy('date_signed')">
            Date Signed
          </th>
          <th>Task</th>
          <th>
            <input type="checkbox" id="checkall" @click="checkAll" v-model="allChecked">
          </th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="part in sortedParticipants" :key="'part_'+part.id">
          <td>
            <a :href="'/participants/'+part.id">{{ part.ccs_id }}</a>
          </td>
          <td>
            <a :href="'/participants/'+part.id">{{ part.full_name }}</a>
          </td>
          <td>{{ part.age }}</td>
          <td>{{ part.gender }}</td>
          <td>{{ part.language }}</td>
          <td>{{ part.sab }}</td>
          <td>{{ part.serostatus }}</td>
          <td>{{ part.latest_signed }}</td>
          <td>{{ part.date_signed }}</td>
          <td>
            <span v-if="part.pending_task_id">
              <a :href="'/tasks/'+ part.pending_task_id">{{ part.pending_task_name }}</a>
              <div style="display: inline-block; position: relative;">
                <button 
                  type="button" 
                  class="btn btn-xs btn-default" 
                  title="Cancel, not needed"
                  v-on:click="cancelConsentTask(part.full_name, part.pending_task_id)"
                  :disabled="cancelingTaskId == part.pending_task_id"
                  >
                  {{ cancelingTaskId == part.pending_task_id ? 'Canceling...' : 'Cancel' }}
                </button>
              </div>
            </span>
            <span v-else>
              <button class="btn btn-default" @click="checkedParticipants.push(part.id); assignConsentTask()">Assign Consent Task</button>
            </span>
          </td>
          <td>
            <input type="checkbox" :id="'checkbox_'+part.id" :value="part.id" v-model="checkedParticipants" number>
          </td>
        </tr>
      </tbody>
    </table>

  </div><!-- !loadingConsent -->

  <assign-task
    v-model:showModal="showAssignTask"
    :consent="consent"
    :tasks="tasks"
    v-on:confirm="saveTask"
    v-on:closed="showAssignTask=!showAssignTask"
  ></assign-task>
   
  <cancel-task
    v-model:showModal="showCancelTask"
    :consent="consent"
    :participant="cancelingTaskParticipant"
    v-on:confirm="confirmCancel"
    v-on:closed="closeCancel"
  ></cancel-task>

  <create-version
    v-model:showModal="showCreateVersion"
    :consent="consent"
    v-on:confirm="saveChanges"
    v-on:closed="showCreateVersion=!showCreateVersion"
  ></create-version>

  <create-addendum
    v-model:showModal="showCreateAddendum"
    :consent="consent"
    :versions="versions"
    v-on:confirm="saveChanges"
    v-on:closed="showCreateAddendum=!showCreateAddendum"
  ></create-addendum>

  <div class="alert alert-info floating-feedback" v-if="message">
    <small>{{ message }}</small>
  </div>
        
</template>

<style lang="scss">
    @import "../../../sass/consents";
</style>

<script setup>
  import { ref, onMounted, computed } from 'vue'
  import AssignTask from './modals/AssignTask.vue'
  import CancelTask from './modals/CancelTask.vue'
  import CreateAddendum from './modals/CreateAddendum.vue'
  import CreateVersion from './modals/CreateVersion.vue'

  const props = defineProps(['consentFormId'])

  const loadingConsent = ref(true)
  const loadingParticipants = ref(true)
  const addVersionForm = ref(false)
  const showCreateVersion = ref(false)
  const addAddendumForm = ref(false)
  const showCreateAddendum = ref(false)
  const showAssignTask = ref(false)
  const showCancelTask = ref(false)
  const cancelingTaskId = ref(false)
  const cancelingTaskParticipant = ref(false)
  const allChecked = ref(false)
  const participants = ref([])
  const tasks = ref([])
  const versions = ref([])
  const checkedParticipants = ref([])
  const filteredParticipants = ref([])
  const filter = ref(null)
  const sortBy = ref('ccs_id')
  const sortDir = ref('asc')
  const message = ref(null)
  const consent = ref({ latestVersion: {}, previousVersion: {}, })

  onMounted(() => {
    getConsent()
    getConsentParticipants()
  })

  function setSortBy(val) {
    sortBy.value = val
    sortDir.value = sortDir.value == 'asc' ? 'desc' : 'asc'
  }

  function addVersion() {
    addVersionForm.value = true
    showCreateVersion.value = true
  }

  function addAddendum() {
    versions.value = consentVersions()
    addAddendumForm.value = true
    showCreateAddendum.value = true
  }

  function consentVersions() {
    let consentVersions = []
    consent.value.consentForms.forEach(function(consentForm) {
      consentVersions.push(consentForm.version)
    })
    return consentVersions
  }

  function assignConsentTask() {
    tasks.value = consent.value.latest_two
    showAssignTask.value = true
  }

  function saveTask(data) {
    // if filter exists, keep only participants who are both checked and filtered
    let checkedIds = []
    if (sortedParticipants.value.length > 0) {
      let checked = sortedParticipants.value.filter(x => checkedParticipants.value.includes(x.id))
      checkedIds = checked.map(x => x.id) 
    } else {
     checkedIds = checkedParticipants.value
    }

    showAssignTask.value = false
    axios.post('/api/consent-form-participant/assign-task', {
      'consent_form_type_id': data.consent_form_type_id,
      'participant_ids': checkedIds,
      'version': data.version
    })
    .then(function (response) {
      getConsentParticipants()
      allChecked.value = false
      versions.value = checkedParticipants.value = []
    }.bind(this))
    .catch(function (error) {
      console.error(error)
      alert('there was a problem assigning your task(s)')
    })
  }

  function saveChanges(data) {
    showCreateVersion.value = showCreateAddendum.value = false
    loadingConsent.value = true
    let route = ''
    if (addVersionForm.value) { route = '/api/consent-forms' }
    if (addAddendumForm.value) { route = '/api/consent-form-addendums' }
    axios.post(route, data)
        .then(function (response) {
          getConsent()
          loadingConsent.value = false
          addVersionForm.value = addAddendumForm.value = false
        }.bind(this))
        .catch(function (error) {
          console.error(error)
          alert('there was a problem saving your consent changes')
        })
    }

  function checkAll() {
    checkedParticipants.value = []
    let checked = []
    let parts = sortedParticipants.value

    if (!allChecked.value) {
      parts.forEach(function (participant) {
        checked.push(participant.id)
      })
      checkedParticipants.value = checked
      allChecked.value = true
    } else {
      allChecked.value = false
    }
  }

  function triggerMessage(msg) {
    message.value = msg
    setTimeout(function () {
      message.value = null
    }.bind(this), 4000)
  }

  function closeCancel() {
    cancelingTaskId.value = false
    cancelingTaskParticipant.value = false
    showCancelTask.value = false
  }

  function cancelConsentTask(participantName, taskId) {
    cancelingTaskId.value = taskId
    cancelingTaskParticipant.value = participantName
    showCancelTask.value = true
  }

  function confirmCancel() {
    showCancelTask.value = false
    return axios.put('/api/tasks/'+cancelingTaskId.value, { task_status_id: 5 })
      .then(function (response) {
        triggerMessage('Consent task canceled for '+cancelingTaskParticipant.value)
        participants.value = []
        cancelingTaskId.value = false
        cancelingTaskParticipant.value = false
        getConsentParticipants()
      }.bind(this))
      .catch(function () {
        alert('There was a problem canceling the upcoming task');
      });
  }

  function getConsent() {
    axios.get('/api/consent-form-types/'+ props.consentFormId)
    .then(function (response) {
      consent.value = response.data.data
      consent.value.latestVersion = consent.value.latest_two[0] || {};
      consent.value.previousVersion = consent.value.latest_two[1] || {};
      loadingConsent.value = false
    }.bind(this))
    .catch(function () {
      alert('Failed to get consent.')
    });
  }

  function getConsentParticipants() {  
    loadingParticipants.value = true
    axios.get('/api/consent-forms/'+ props.consentFormId +'/participants')
        .then(function (response) {
          participants.value = response.data.data
          loadingParticipants.value = false
        }.bind(this))
        .catch(function () {
          alert('Failed to get participants')
        });
  }

  const sortedParticipants = computed(() => {
    var filterFunc = function(part) {
      var re = new RegExp(filter.value, 'i');
      if (filter.value && filter.value.length > 1) {
        return (part.full_name.search(re) != -1
            || part.ccs_id == filter
            || part.age == filter
            || part.serostatus.search(re) != -1
            || part.language.search(re) != -1
            || part.sab.search(re) != -1
            || part.gender.search(re) != -1
            || (part.latest_signed && part.latest_signed.search(re) != -1)
            || (part.latest_signed && part.date_signed.search(re) != -1));
      } else {
        return true
      }
    }.bind(this);

    let direction = sortDir.value == 'asc' ?  1 : -1;
    return participants.value.filter(filterFunc)
        .sort(function (a, b) {
          if (a[sortBy.value] > b[sortBy.value]) return 1 * direction;
          if (a[sortBy.value] < b[sortBy.value]) return -1 * direction;
          return 0;
        }.bind(this));
  })
</script>

<style scoped>
  th {
    cursor: pointer;
  }
</style>